import { useLayoutEffect, useRef } from "react";
import gsap from "gsap";
import styles from "./SizeRuler.module.css";

const MIN_INCHES = 0.5;
const MAX_INCHES = 24;
const TICK_SPACING = 12;
const clamp = (value) => Math.min(MAX_INCHES, Math.max(MIN_INCHES, value));

export function formatTattooSize(value, unit) {
  if (!value) return "";
  const measurement = parseFloat(value) * (unit === "cm" ? 2.54 : 1);
  return `${Number(measurement.toFixed(unit === "cm" ? 1 : 2))} ${unit === "cm" ? "cm" : "inches"}`;
}

export default function SizeRuler({ value, onChange, unit = "in", onUnitChange, error }) {
  const drag = useRef(null);
  const track = useRef(null);
  const previousUnit = useRef(null);
  // Keep the underlying measurement in inches so unit switches never round it.
  const inches = value ? parseFloat(value) : 4;
  const factor = unit === "cm" ? 2.54 : 1;
  const step = unit === "cm" ? 0.1 : 0.25;
  const measurement = inches * factor;
  const min = MIN_INCHES * factor;
  const max = MAX_INCHES * factor;
  const decimals = unit === "cm" ? 1 : 2;
  const formatted = Number(measurement.toFixed(decimals));

  useLayoutEffect(() => {
    const x = -measurement / step * TICK_SPACING;
    const reducedMotion = window.matchMedia?.("(prefers-reduced-motion: reduce)").matches;
    // A new unit changes tick spacing; position it immediately to avoid
    // sweeping through unrelated measurements on the replacement scale.
    const immediate = previousUnit.current !== unit || !value || reducedMotion;
    previousUnit.current = unit;
    const animation = immediate
      ? gsap.set(track.current, { x })
      : gsap.to(track.current, {
          x,
          duration: drag.current ? 0.16 : 0.3,
          ease: "power3.out",
          overwrite: true,
        });
    return () => animation.kill();
  }, [measurement, step, unit, value]);

  const select = (next) => {
    const rounded = Math.round(next / step) * step;
    onChange(`${Number(clamp(rounded / factor).toFixed(6))} inches`);
  };
  const handleKeyDown = (event) => {
    const changes = {
      ArrowRight: step, ArrowUp: step,
      ArrowLeft: -step, ArrowDown: -step,
      PageUp: step * 10, PageDown: -step * 10,
    };
    if (event.key === "Home" || event.key === "End") {
      event.preventDefault();
      onChange(`${event.key === "Home" ? MIN_INCHES : MAX_INCHES} inches`);
    } else if (changes[event.key] !== undefined) {
      event.preventDefault();
      select(measurement + changes[event.key]);
    } else if (event.key === "Enter" || event.key === " ") {
      event.preventDefault();
      select(measurement);
    }
  };
  const firstTick = Math.ceil(min / step);
  const lastTick = Math.floor(max / step);

  return (
    <fieldset className={styles.field}>
      <legend id="tattooSize-label">Tattoo Size *</legend>
      <p className={styles.help} id="tattooSize-help">
        Estimate the longest side. Drag the ruler or use the arrow keys to choose a size.
      </p>
      <div className={styles.card}>
        <div className={styles.units} role="group" aria-label="Size units">
          {[ ["in", "Inches"], ["cm", "Centimeters"] ].map(([key, label]) => (
            <button key={key} type="button" aria-pressed={unit === key}
              onClick={() => onUnitChange(key)}>{label}</button>
          ))}
        </div>
        <div className={styles.readout} aria-hidden="true">
          {formatted}<span>{unit}</span>
        </div>
        <div
          className={styles.ruler}
          role="slider"
          tabIndex={0}
          aria-labelledby="tattooSize-label"
          aria-describedby={`tattooSize-help${error ? " tattooSize-error" : ""}`}
          aria-valuemin={min}
          aria-valuemax={max}
          aria-valuenow={measurement}
          aria-valuetext={`${formatted} ${unit === "cm" ? "centimeters" : "inches"}${value ? "" : ", choose a size"}`}
          aria-invalid={Boolean(error)}
          onKeyDown={handleKeyDown}
          onPointerDown={(event) => {
            if (!event.isPrimary || event.button !== 0) return;
            event.currentTarget.focus();
            event.currentTarget.setPointerCapture(event.pointerId);
            drag.current = { x: event.clientX, measurement };
            select(measurement);
          }}
          onPointerMove={(event) => {
            if (!drag.current) return;
            select(drag.current.measurement + (drag.current.x - event.clientX) / TICK_SPACING * step);
          }}
          onPointerUp={() => { drag.current = null; }}
          onPointerCancel={() => { drag.current = null; }}
          onLostPointerCapture={() => { drag.current = null; }}
        >
          <div ref={track} className={styles.track} aria-hidden="true">
            {Array.from({ length: lastTick - firstTick + 1 }, (_, index) => {
              const tick = index + firstTick;
              const major = tick % (unit === "cm" ? 10 : 4) === 0;
              return <div key={tick} className={`${styles.tick} ${major ? styles.major : ""}`}
                style={{ left: tick * TICK_SPACING }}>
                {major && <span>{Number((tick * step).toFixed(1))}</span>}
              </div>;
            })}
          </div>
          <div className={styles.marker} aria-hidden="true" />
        </div>
        <p className={styles.hint}>{value ? "Approximate size" : "Drag or press an arrow key to select"}</p>
      </div>
      {error && <p id="tattooSize-error" className={styles.error}>{error}</p>}
    </fieldset>
  );
}
